// The one bar across the top of the signed-in app.
//
// It carries, left to right: the brand, the favourite accounts, search, the
// workspace being acted in, the plan, the account, and — last, in the top-right
// corner the specification asks for — the control that opens and closes the
// navigation.
//
// The workspace switcher is shown only when there is a choice to make: one
// workspace needs no switcher, several make acting in the wrong one a real risk.
//
// The brand switcher beside it is a different kind of control and follows a
// different rule: it is shown whenever the workspace has a brand, including the
// single one every workspace is born with. A workspace is who you are billed as;
// a brand is who you are publishing as, and that second answer is worth having
// on screen even when there is only one of it (ADR-0029). It filters the view
// and grants nothing.

import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { BrandMark } from "../components/BrandMark";
import { Monogram } from "../components/Monogram";
import { COMPANY } from "../lib/company";
import { DASHBOARD_ROUTE } from "../dashboard";
import { MenuIcon } from "./icons";
import { FavouritesMenu } from "./FavouritesMenu";
import { JumpSearch } from "./JumpSearch";
import { TopFavoritesBar } from "./TopFavoritesBar";
import type { FavouritesState } from "./state";
import type { Brand } from "../lib/brands";
import type { WorkspaceSummary } from "../lib/workspace";

interface Props {
  toolbarExtras?: ReactNode;
  session: Session;
  workspaces: WorkspaceSummary[];
  active: WorkspaceSummary | null;
  onSelectWorkspace: (id: string) => void;
  /** The active workspace's brands. Empty while they load, or when the list
   *  could not be read — in which case no switcher appears and every account
   *  stays visible, rather than a filter nobody chose hiding half of them. */
  brands: Brand[];
  /** Null = every brand. */
  activeBrandId: string | null;
  onSelectBrand: (id: string | null) => void;
  /** The top bar's favourites. The rail keeps a separate list of its own. */
  favourites: FavouritesState;
  connectionsLoading: boolean;
  navOpen: boolean;
  onToggleNav: () => void;
  /** What the control does here — it removes the menu column on a desktop and
   *  opens a drawer over the page on a phone — so the accessible name is the
   *  truth in both. */
  navControls: "sidebar" | "drawer";
}

export function AppHeader({
  session, workspaces, active, onSelectWorkspace, brands, activeBrandId, onSelectBrand,
  favourites, connectionsLoading, navOpen, onToggleNav, navControls, toolbarExtras,
}: Props) {
  // The same words as the twin control in the favourites rail: two toggles for
  // one thing must not describe it two ways.
  const label = navControls === "drawer"
    ? (navOpen ? "Close menu" : "Open menu")
    : (navOpen ? "Hide navigation" : "Show navigation");

  return (
    <header className="app-header">
      <Link to={DASHBOARD_ROUTE} className="app-brand" title={`${COMPANY.product} — dashboard`}>
        <BrandMark />
        <span className="app-brand-name">{COMPANY.product}</span>
      </Link>

      <TopFavoritesBar favourites={favourites.favourites} loading={connectionsLoading} />

      <span className="spacer" />

      <JumpSearch candidates={favourites.candidates} />

      {workspaces.length > 1 && (
        <label className="header-switcher-label">
          <span>WorkSpace</span>
          <select
            className="workspace-switcher"
            value={active?.workspace_id ?? ""}
            onChange={(e) => onSelectWorkspace(e.target.value)}
          >
            {workspaces.map((w) => (
              <option key={w.workspace_id} value={w.workspace_id}>{w.name}</option>
            ))}
          </select>
        </label>
      )}

      {/* "All brands" is first and is the empty value, so the control reads as
          a filter that is off rather than as a brand called "All". Managing the
          list lives on the Connections screen, beside the accounts being filed —
          this is the switcher, not a second place to edit the same thing. */}
      {brands.length > 0 && (
        <label className="header-switcher-label">
          <span>Brand</span>
          <select
            className="brand-switcher"
            title="Which brand's accounts to show"
            value={activeBrandId ?? ""}
            onChange={(e) => onSelectBrand(e.target.value || null)}
          >
            <option value="">All brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>
      )}

      {/* Beside the plan rather than at the end of the row it edits. The row
          scrolls when it is full, and a control that scrolls away is the one
          control that must not — it is the only way to empty the row again.
          Icon alone, because it sits in the cluster of small round controls at
          this end of the bar and a word here would be the only one. The rail's
          twin lives at the foot of the rail, on the list it changes. */}
      <FavouritesMenu
        candidates={favourites.candidates}
        isFavourite={favourites.isFavourite}
        toggle={favourites.toggle}
        customised={favourites.customised}
        reset={favourites.reset}
        fixed={favourites.fixed}
        count={favourites.favourites.length}
        surfaceName="top bar"
      />

      {toolbarExtras}

      <Link to="/plans" className={`plan-chip${active && active.plan !== "free" ? " paid" : ""}`}
            title="Your plan — click to see pricing">
        {active?.plan ? active.plan.charAt(0).toUpperCase() + active.plan.slice(1) : "Free"}
      </Link>

      {/* The account, reduced to its monogram: the bar is for getting
          somewhere, and a full address spends a lot of width saying something
          the person already knows. The address stays in the tooltip and the
          accessible name, and the Profile screen it opens shows it in full. */}
      <Link to="/profile" className="user-chip"
            title={session.user.email}
            aria-label={session.user.email ? `Your account — ${session.user.email}` : "Your account"}>
        <Monogram label={session.user.email ?? "?"} className="monogram-sm" />
      </Link>

      <button type="button" className="nav-toggle" onClick={onToggleNav}
              aria-label={label} title={label}
              aria-expanded={navOpen} aria-controls="app-sidebar">
        <MenuIcon className="nav-icon" />
      </button>
    </header>
  );
}
